import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { prescriptionAPI } from '../../../utils/api';
import { useNotification } from '../../../context/NotificationContext';
import { ArrowLeft, Download, FileText, Calendar, User, Activity, Package } from 'lucide-react';

const PatientPrescriptionView = () => {
    const { consultationId } = useParams();
    const navigate = useNavigate();
    const { showError } = useNotification();
    const [prescription, setPrescription] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPrescription();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consultationId]);

    const fetchPrescription = async () => {
        try {
            const res = await prescriptionAPI.getPrescriptionByConsultation(consultationId);
            if (res.data?.success) {
                setPrescription(res.data.data);
            } else {
                showError('Recommendations not found');
            }
        } catch (error) {
            console.error(error);
            showError('Failed to load prescription');
        } finally {
            setLoading(false);
        }
    };

    const downloadPDF = () => {
        if (!prescription?._id) return;

        // Construct download URL
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api';

        // We can't easily add headers to window.open, so we might need a signed URL or 
        // rely on the browser session if cookies were used (they are not).
        // Alternatively, use fetch with blob.

        // Simple approach: Use a direct link if auth allows query param token (not secure usually)
        // OR: Use the download endpoint and hopefully the browser handles the auth challenge (it won't).

        // Better approach: Fetch as blob and download
        const downloadUrl = `${baseUrl}/prescription/download/${prescription._id}`;

        fetch(downloadUrl, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
            .then(async response => {
                if (!response.ok) {
                    const text = await response.text();
                    let errMsg = 'Download failed';
                    try {
                        const parsed = JSON.parse(text);
                        errMsg = parsed.message || errMsg;
                    } catch (e) { }
                    throw new Error(errMsg);
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Recommendations-${prescription.doctorDetails?.name || 'Vivara'}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            })
            .catch(err => {
                console.error(err);
                showError('Failed to download PDF');
            });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-dark flex items-center justify-center text-gold">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
            </div>
        );
    }

    if (!prescription) {
        return (
            <div className="min-h-screen bg-dark text-light p-6 flex flex-col items-center justify-center">
                <FileText className="w-16 h-16 text-gray-600 mb-4" />
                <h2 className="text-xl font-semibold mb-2">No Recommendations Found</h2>
                <button
                    onClick={() => navigate(-1)}
                    className="text-gold hover:underline mt-4"
                >
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-dark text-light pb-20">
            {/* Header */}
            <div className="bg-gray-800 p-4 sticky top-0 z-10 shadow-md flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-700 rounded-full">
                        <ArrowLeft className="w-6 h-6 text-gold" />
                    </button>
                    <h1 className="text-lg font-bold">Recommendation Details</h1>
                </div>
                <button
                    onClick={downloadPDF}
                    className="flex items-center gap-2 bg-gold text-dark px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg hover:bg-yellow-500"
                >
                    <Download className="w-4 h-4" />
                    PDF
                </button>
            </div>

            <div className="p-4 space-y-6 max-w-2xl mx-auto">
                {/* Doctor & Patient Info */}
                <div className="bg-gray-800 rounded-xl p-4 shadow-sm">
                    <div className="flex justify-between items-start border-b border-gray-700 pb-4 mb-4">
                        <div className="flex items-start gap-3">
                             <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center text-2xl">👤</div>
                            <div>
                                <h2 className="font-bold text-lg text-gold">{prescription.doctorDetails?.name}</h2>
                                <p className="text-sm text-gray-400">{prescription.doctorDetails?.specialization}</p>
                                <p className="text-xs text-gray-500 mt-1">Lic: {prescription.doctorDetails?.licenseNumber}</p>
                            </div>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                            <p>{new Date(prescription.createdAt).toLocaleDateString()}</p>
                            <p>{new Date(prescription.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="bg-gray-700 p-2 rounded-full"><User className="w-4 h-4 text-gold" /></div>
                        <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide">User</p>
                            <p className="font-medium">{prescription.patientDetails?.name} ({prescription.patientDetails?.age}y / {prescription.patientDetails?.gender})</p>
                        </div>
                    </div>
                </div>

                {/* Vitals & Diagnosis */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-800 rounded-xl p-4">
                        <h3 className="text-gold text-sm font-bold uppercase mb-3 flex items-center gap-2">
                            <Activity className="w-4 h-4" /> Assessment
                        </h3>
                        <p className="text-sm">{prescription.diagnosis || 'Not specified'}</p>
                        {prescription.chiefComplaint && (
                            <div className="mt-3 pt-3 border-t border-gray-700">
                                <p className="text-xs text-gray-400 mb-1">Chief Complaint</p>
                                <p className="text-sm italic">"{prescription.chiefComplaint}"</p>
                            </div>
                        )}
                    </div>

                    {prescription.vitals && Object.keys(prescription.vitals).length > 0 && (
                        <div className="bg-gray-800 rounded-xl p-4">
                            <h3 className="text-gold text-sm font-bold uppercase mb-3">Vitals</h3>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                {Object.entries(prescription.vitals).map(([key, value]) => (
                                    value && <div key={key} className="flex flex-col">
                                        <span className="text-gray-400 text-xs capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                        <span className="font-medium">{value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Medicines */}
                <div className="bg-gray-800 rounded-xl p-4">
                    <h3 className="text-gold text-sm font-bold uppercase mb-4 flex items-center gap-2">
                        <Activity className="w-4 h-4" /> Products
                    </h3>
                    <div className="space-y-4">
                        {prescription.medicines?.map((med, idx) => (
                            <div key={idx} className="bg-gray-700/50 rounded-lg p-3 relative">
                                <span className="absolute top-3 right-3 text-xs font-mono bg-gray-800 px-2 py-0.5 rounded text-gold border border-gold/30">
                                    {med.type || 'Tablet'}
                                </span>
                                <h4 className="font-bold text-base mb-1">{med.name}</h4>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-300">
                                    <span>{med.dosage}</span>
                                    <span className="text-gray-500">•</span>
                                    <span>{med.frequency}</span>
                                    <span className="text-gray-500">•</span>
                                    <span>{med.duration} days</span>
                                </div>
                                {med.instruction && (
                                    <p className="text-xs text-gray-400 mt-2 italic">Note: {med.instruction}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Advice & Follow-up */}
                {(prescription.generalInstructions || prescription.follow_up_date) && (
                    <div className="bg-gray-800 rounded-xl p-4">
                        {prescription.generalInstructions && (
                            <div className="mb-4">
                                <h3 className="text-gold text-sm font-bold uppercase mb-2">Advice</h3>
                                <p className="text-sm whitespace-pre-wrap">{prescription.generalInstructions}</p>
                            </div>
                        )}

                        {prescription.follow_up_date && (
                            <div className="flex items-center gap-3 bg-blue-900/20 p-3 rounded-lg border border-blue-900/50">
                                <Calendar className="w-5 h-5 text-blue-400" />
                                <div>
                                    <p className="text-xs text-blue-300">Follow-up Date</p>
                                    <p className="font-bold text-blue-100">
                                        {new Date(prescription.follow_up_date).toLocaleDateString(undefined, {
                                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                                        })}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Digital Signature */}
                <div className="flex justify-end mt-8">
                    <div className="text-center">
                        {prescription.digital_signature && (
                            <img
                                src={prescription.digital_signature}
                                alt="Doctor Signature"
                                className="h-16 object-contain mb-2 opacity-80"
                            />
                        )}
                        <p className="text-xs text-gray-500">
                            Digitally Signed by {prescription.doctorDetails?.name}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PatientPrescriptionView;
